import { ParticipantId, SessionParticipant, SessionTranscriptEntry } from '../types/superadmin';

/**
 * Display helpers for a session's transcript.
 *
 * Mirrors `admin-portal/src/config/participants.ts`, minus the Tailwind colour
 * palettes: the super-admin portal is token-driven, so a speaker resolves to a
 * CSS class here rather than a hex value.
 *
 * Two orthogonal axes, easy to conflate:
 *   - `speaker` role — 'salesperson' | 'customer'. Drives grading.
 *   - participant identity — `participantId` + `speakerName` + the session
 *     roster. Drives attribution and display.
 *
 * `participantId` is a string on bot/live sessions (`customer_0`) and a NUMBER
 * on desktop-captured ones (100, 200). Compared loosely for that reason.
 */

const SALESPERSON = 'salesperson';
const CUSTOMER = 'customer';

/** How many distinct customer colour slots exist in the stylesheet. */
const CUSTOMER_SLOTS = 5;

const sameId = (a?: ParticipantId | null, b?: ParticipantId | null): boolean =>
  a != null && b != null && String(a) === String(b);

const idsWithRole = (roster: SessionParticipant[] | undefined, role: string): ParticipantId[] =>
  (roster || []).filter((p) => p?.role === role && p?.participantId != null).map((p) => p.participantId as ParticipantId);

/** 0-based position among the roster's customers; 0 for anything unknown. */
export function customerIndex(
  participantId: ParticipantId | null | undefined,
  roster?: SessionParticipant[]
): number {
  if (participantId == null) return 0;
  const index = idsWithRole(roster, CUSTOMER).findIndex((id) => sameId(id, participantId));
  return index >= 0 ? index : 0;
}

/** 0-based position among the roster's salespeople (team-selling calls). */
export function salespersonIndex(
  participantId: ParticipantId | null | undefined,
  roster?: SessionParticipant[]
): number {
  if (participantId == null) return 0;
  const index = idsWithRole(roster, SALESPERSON).findIndex((id) => sameId(id, participantId));
  return index >= 0 ? index : 0;
}

export const isSalesperson = (entry: Pick<SessionTranscriptEntry, 'speaker'>): boolean =>
  entry?.speaker === SALESPERSON;

/**
 * Display name for a transcript entry. The ROSTER wins over the entry's own
 * `speakerName`: each transcript freezes a name at receive time, but the roster
 * is the single editable source, so a rename must reach already-received lines
 * too.
 */
export function speakerLabel(
  entry: SessionTranscriptEntry,
  roster?: SessionParticipant[]
): string {
  const rosterName = (roster || []).find((p) => sameId(p?.participantId, entry?.participantId))?.name;

  if (isSalesperson(entry)) {
    // 'Salesperson' is the analyzer's placeholder, not a person's name.
    if (rosterName && rosterName.trim() && rosterName.trim() !== 'Salesperson') return rosterName;
    return 'Salesperson';
  }

  if (rosterName && rosterName.trim()) return rosterName;
  if (entry?.speakerName && entry.speakerName.trim()) return entry.speakerName;
  return `Customer ${customerIndex(entry?.participantId, roster) + 1}`;
}

/**
 * CSS modifier for a transcript bubble: `sp` / `sp-2…` for salespeople,
 * `c0…c4` for customers. The stylesheet maps these onto the portal's tokens.
 */
export function speakerClass(
  entry: SessionTranscriptEntry,
  roster?: SessionParticipant[]
): string {
  if (isSalesperson(entry)) {
    const index = salespersonIndex(entry?.participantId, roster);
    return index === 0 ? 'sp' : 'sp-2';
  }
  return 'c' + (customerIndex(entry?.participantId, roster) % CUSTOMER_SLOTS);
}

/** 'Aug 7, 15:18:26' — transcripts are stored UTC, so they are read as UTC. */
export function transcriptTime(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('en-GB', { timeZone: 'UTC', hour12: false });
}
